import React from "react";
import { Link } from "react-router-dom";
import {
  Award, Users, Truck, MapPin, ShieldCheck, ArrowRight,
  Route, Package, Repeat, ClipboardList, Users2, FileCheck, MoveVertical,
  Layers, Satellite, MessagesSquare, CheckCircle2
} from "lucide-react";
import Reveal from "../components/Reveal";
import { Button } from "../components/Button";
import { COMPANY, WHY_CHOOSE, CLIENTS, CERTIFICATIONS, IMAGES, FLEET } from "../data";

const WHY_ICONS = { Truck, Award, Layers, Satellite, MessagesSquare, ShieldCheck };

const HOME_SERVICES = [
  { key: "route-survey", title: "Route Survey & Planning", body: "Detailed route analysis, surveys and planning for over-dimensional cargo movements.", icon: Route },
  { key: "odc", title: "ODC Transportation", body: "Safe and compliant transportation of over-dimensional and heavy cargo across India.", icon: Package },
  { key: "inter-carting", title: "Inter-Carting Operations", body: "Last-mile movement of components from lay-down yards to remote wind sites.", icon: Repeat },
  { key: "project-logistics", title: "Project Logistics Management", body: "Complete logistics management tailored for large renewable energy projects.", icon: ClipboardList },
  { key: "site-coordination", title: "Site Coordination", body: "On-site coordination and execution support with dedicated project supervisors.", icon: Users2 },
  { key: "escort-permits", title: "Escort & Permit Management", body: "Handling permits, escorts and regulatory approvals across multiple Indian states.", icon: FileCheck },
  { key: "loading-unloading", title: "Loading & Unloading Support", body: "Professional loading and unloading with certified rigging crews and lifting equipment.", icon: MoveVertical },
];

const STATS = [
  { icon: Award, top: "SINCE", big: "2007", bottom: "Years of Excellence" },
  { icon: Users, top: "", big: "25+", bottom: "Years of Industry Experience" },
  { icon: MapPin, top: "", big: "PAN INDIA", bottom: "Operations Across Diverse Terrains" },
  { icon: ShieldCheck, top: "", big: "100%", bottom: "Safety & On-time Delivery" },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO — split layout (40 / 60) with image-driven natural height */}
      <section className="relative bg-white" data-testid="home-hero">
        <div className="grid grid-cols-1 lg:grid-cols-[40fr_60fr] items-center gap-6 lg:gap-0">
          {/* LEFT */}
          <div className="relative px-6 md:px-12 lg:px-12 xl:px-16 py-12 md:py-16 lg:py-8 flex flex-col justify-center">
            <Reveal>
              <h1
                className="font-heading uppercase leading-[1.0] tracking-tight text-[30px] sm:text-[42px] md:text-[46px] xl:text-[54px] 2xl:text-[62px]"
                data-testid="home-hero-headline"
              >
                <span className="block text-brand-green">Specialized</span>
                <span className="block text-brand-green">
                  Transportation <span className="text-brand-orange">&amp;</span>
                </span>
                <span className="block text-brand-orange">Logistics Solutions</span>
                <span className="block text-brand-green">for India's</span>
                <span className="block text-brand-green">Wind Energy Sector</span>
              </h1>
            </Reveal>

            <Reveal delay={150}>
              <div className="mt-6 flex items-center gap-4">
                <span className="block w-14 h-1 bg-brand-orange" />
              </div>
            </Reveal>

            <Reveal delay={250}>
              <p className="mt-5 font-body text-base md:text-[17px] text-zinc-700 leading-relaxed max-w-xl">
                MRL Transports specializes in the transportation of wind turbine components,
                over-dimensional cargo, inter-carting operations and project logistics across India.
              </p>
            </Reveal>

            <Reveal delay={350}>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Link to="/services" data-testid="home-hero-cta-services">
                  <Button variant="secondary" size="lg">
                    Explore Services <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/contact" data-testid="home-hero-cta-contact">
                  <Button variant="outline" size="lg" className="!border-brand-green !text-brand-green hover:!bg-brand-green hover:!text-white">
                    Contact Us <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </Reveal>
          </div>

          {/* RIGHT — hero image (60%), fills column, natural aspect */}
          <div className="relative w-full self-stretch flex items-center bg-white" data-testid="home-hero-image">
            <picture className="block w-full">
              <source srcSet="/hero-truck.webp" type="image/webp" />
              <img
                src="/hero-truck.png"
                alt="MRL Transports Scania heavy-haul truck carrying a wind turbine blade on a multi-axle hydraulic trailer with wind turbines in the background"
                width="1742"
                height="1000"
                loading="eager"
                decoding="async"
                fetchpriority="high"
                className="w-full h-auto block object-contain"
              />
            </picture>
            {/* Subtle white gradient on left edge for text readability */}
            <div
              className="absolute inset-y-0 left-0 w-12 md:w-20 lg:w-28 pointer-events-none hidden lg:block"
              style={{ background: "linear-gradient(to right, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%)" }}
            />
            {/* WIDE LOAD badge */}
            <div className="absolute top-4 right-4 lg:top-6 lg:right-6 bg-white/95 backdrop-blur px-4 py-2 border-l-4 border-brand-orange shadow-lg" data-testid="hero-image-badge">
              <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Wide Load</p>
              <p className="font-heading text-sm text-brand-green">MRL Blade Transport</p>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BAR — 5 stat blocks on green */}
      <section className="bg-brand-green text-white" data-testid="home-trust-bar">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-8 md:py-10 grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-4">
          {STATS.map((s, i) => {
            const Icon = s.icon;
            return (
              <Reveal key={s.bottom} delay={i * 80}>
                <div
                  className="flex items-center gap-4 md:gap-5"
                  data-testid={`trust-stat-${i}`}
                >
                  <div className="w-14 h-14 md:w-16 md:h-16 shrink-0 flex items-center justify-center border-2 border-brand-orange">
                    <Icon className="w-7 h-7 md:w-8 md:h-8 text-brand-orange" />
                  </div>
                  <div className="min-w-0">
                    {s.top && (
                      <p className="font-mono text-[10px] md:text-xs uppercase tracking-widest text-white/80">{s.top}</p>
                    )}
                    <p className="font-heading text-xl md:text-2xl xl:text-3xl leading-none tracking-tight">{s.big}</p>
                    <p className="font-body text-[11px] md:text-xs mt-1 text-white/90 leading-snug">{s.bottom}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* SERVICES — 7 icon cards */}
      <section className="py-20 md:py-28 bg-brand-smoke" data-testid="home-services-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <Reveal>
            <div className="flex items-center justify-center gap-3">
              <span className="block w-10 h-[2px] bg-brand-orange" />
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-brand-orange">Our Services</p>
              <span className="block w-10 h-[2px] bg-brand-orange" />
            </div>
          </Reveal>
          <Reveal delay={100}>
            <h2 className="font-heading text-3xl md:text-5xl xl:text-6xl leading-[1.05] tracking-tight text-center text-brand-ink mt-4 max-w-4xl mx-auto">
              End-to-End Logistics Solutions<br />for Every Project
            </h2>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4">
            {HOME_SERVICES.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.key} delay={i * 70}>
                  <Link
                    to="/services"
                    data-testid={`home-service-card-${s.key}`}
                    className="group block bg-white border-2 border-zinc-200 hover:border-brand-orange hover:-translate-y-1 transition-all p-6 h-full text-center"
                  >
                    <div className="w-14 h-14 mx-auto flex items-center justify-center border-2 border-brand-green group-hover:bg-brand-green transition-colors">
                      <Icon className="w-6 h-6 text-brand-green group-hover:text-white" />
                    </div>
                    <h3 className="font-heading text-base md:text-lg mt-5 tracking-tight text-brand-ink leading-tight">
                      {s.title}
                    </h3>
                    <p className="font-body text-xs text-zinc-600 mt-3 leading-relaxed">
                      {s.body}
                    </p>
                  </Link>
                </Reveal>
              );
            })}
          </div>

          <div className="mt-12 flex justify-center">
            <Link to="/services" data-testid="home-services-view-all">
              <Button variant="primary">View All Services <ArrowRight className="w-4 h-4" /></Button>
            </Link>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-24 md:py-32 bg-white" data-testid="home-why-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-16">
            <div className="lg:col-span-5">
              <Reveal><p className="section-tag">Why Choose MRL</p></Reveal>
              <Reveal delay={100}>
                <h2 className="font-heading text-4xl md:text-5xl xl:text-6xl leading-[1.02] tracking-tight mt-6 text-brand-ink">
                  Engineered for <span className="text-brand-green">precision</span>.<br />
                  Built for <span className="text-brand-orange">scale</span>.
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-7 lg:pt-8">
              <Reveal delay={200}>
                <p className="font-body text-lg text-zinc-700 leading-relaxed">
                  Behind every wind energy project is a complex logistics operation that ensures critical components reach
                  their destination safely and on schedule. We combine specialised equipment, deep operational expertise
                  and a safety-first culture to deliver reliable outcomes — project after project.
                </p>
              </Reveal>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-l border-zinc-200">
            {WHY_CHOOSE.map((w, i) => {
              const Icon = WHY_ICONS[w.icon] || Truck;
              return (
                <Reveal key={w.title} delay={i * 80}>
                  <div
                    className="group p-8 md:p-10 border-r border-b border-zinc-200 hover:bg-brand-green hover:text-white transition-all duration-300 min-h-[260px] flex flex-col"
                    data-testid={`why-card-${i}`}
                  >
                    <div className="w-14 h-14 flex items-center justify-center border-2 border-brand-green group-hover:border-white group-hover:bg-brand-orange transition-all">
                      <Icon className="w-6 h-6 text-brand-green group-hover:text-white" />
                    </div>
                    <h3 className="font-heading text-xl md:text-2xl mt-6 tracking-tight">{w.title}</h3>
                    <p className="font-body text-sm mt-3 leading-relaxed text-zinc-600 group-hover:text-zinc-100 flex-1">
                      {w.body}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* FLEET STRIP */}
      <section className="py-24 md:py-32 bg-brand-ink text-white relative overflow-hidden" data-testid="home-fleet-section">
        <div
          className="absolute right-0 top-0 bottom-0 w-1/2 opacity-25 hidden md:block"
          style={{
            backgroundImage: `url(${IMAGES.fleet})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-ink via-brand-ink/95 to-transparent" />

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12">
            <div className="lg:col-span-7">
              <Reveal><p className="section-tag text-brand-orange">Our Fleet</p></Reveal>
              <Reveal delay={100}>
                <h2 className="font-heading text-4xl md:text-5xl xl:text-6xl leading-[1.02] tracking-tight mt-6">
                  A fleet configured for the heaviest, longest and most complex loads.
                </h2>
              </Reveal>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {FLEET.flatMap((c) => c.items).slice(0, 9).map((f, i) => (
              <Reveal key={f.name} delay={i * 60}>
                <div
                  className="border-2 border-white/20 hover:border-brand-orange p-6 transition-colors bg-white/5 backdrop-blur"
                  data-testid={`home-fleet-item-${i}`}
                >
                  <p className="font-mono text-[10px] uppercase tracking-widest text-brand-orange">Unit {String(i + 1).padStart(2, "0")}</p>
                  <p className="font-heading text-lg mt-3 leading-tight">{f.name}</p>
                  <p className="font-body text-xs text-zinc-400 mt-1">{f.spec}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={400}>
            <div className="mt-12">
              <Link to="/fleet" data-testid="home-fleet-cta">
                <Button variant="secondary">Explore Full Fleet <ArrowRight className="w-4 h-4" /></Button>
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CLIENTS MARQUEE */}
      <section className="py-20 bg-white border-y border-zinc-200 overflow-hidden" data-testid="home-clients-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 mb-10">
          <Reveal><p className="section-tag">Our Valued Clients</p></Reveal>
          <Reveal delay={100}>
            <h2 className="font-heading text-3xl md:text-5xl leading-[1.02] tracking-tight mt-6 text-brand-ink max-w-3xl">
              Trusted by India's leading renewable energy & power companies.
            </h2>
          </Reveal>
        </div>

        <div className="relative">
          <div className="marquee-track">
            {[...CLIENTS, ...CLIENTS].map((c, i) => (
              <div
                key={i}
                className="shrink-0 flex items-center justify-center px-10 py-6 border-r border-zinc-200 min-w-[220px]"
                data-testid={`client-marquee-${i}`}
              >
                <span className="font-heading text-xl md:text-2xl text-brand-ink tracking-tight whitespace-nowrap">
                  {c}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 mt-10">
          <Reveal>
            <Link to="/clients" data-testid="home-clients-cta">
              <Button variant="outline">View All Clients <ArrowRight className="w-4 h-4" /></Button>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* CERTIFICATIONS */}
      <section className="py-24 md:py-32 bg-brand-smoke" data-testid="home-certs-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
            <div className="lg:col-span-6">
              <Reveal><p className="section-tag">Certifications</p></Reveal>
              <Reveal delay={100}>
                <h2 className="font-heading text-4xl md:text-5xl xl:text-6xl leading-[1.02] tracking-tight mt-6 text-brand-ink">
                  Certified. Compliant. Committed.
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-6 lg:pt-8">
              <Reveal delay={200}>
                <p className="font-body text-lg text-zinc-700 leading-relaxed">
                  Quality, safety and environmental responsibility are integral to how we operate.
                  Our ISO-certified management systems ensure international standards across every project.
                </p>
              </Reveal>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {CERTIFICATIONS.map((c, i) => (
              <Reveal key={c.code} delay={i * 100}>
                <div className="bg-white border-2 border-zinc-200 p-8 h-full hover:border-brand-green transition-colors" data-testid={`home-cert-card-${i}`}>
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="w-6 h-6 text-brand-green" />
                    <p className="font-heading text-3xl text-brand-green">{c.code}</p>
                  </div>
                  <p className="font-heading text-lg mt-4 text-brand-ink">{c.title}</p>
                  <p className="font-body text-sm text-zinc-600 mt-3 leading-relaxed">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT CTA */}
      <section className="py-24 md:py-32 bg-brand-green text-white relative overflow-hidden" data-testid="home-cta-section">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `url(${IMAGES.contact})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }} />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <Reveal><p className="section-tag text-white before:bg-brand-orange">Ready to move?</p></Reveal>
            <Reveal delay={100}>
              <h2 className="font-heading text-4xl md:text-5xl xl:text-6xl leading-[1.02] tracking-tight mt-6">
                Plan your next<br />ODC or wind energy project with us.
              </h2>
            </Reveal>
            <Reveal delay={200}>
              <p className="font-body text-lg text-white/90 mt-6 max-w-2xl">
                Share your requirement and our team will respond with fleet availability, feasibility inputs and an indicative quote.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-4 flex flex-col gap-4">
            <Reveal delay={300}>
              <Link to="/contact" data-testid="home-final-cta-quote">
                <Button variant="secondary" size="lg" className="w-full">Request a Quote <ArrowRight className="w-4 h-4" /></Button>
              </Link>
            </Reveal>
            <Reveal delay={400}>
              <a href={`tel:${COMPANY.phones[0].raw}`} data-testid="home-final-cta-call" className="block">
                <Button variant="darkOutline" size="lg" className="w-full">Call {COMPANY.phones[0].value}</Button>
              </a>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
